/**
 * Calendar date math. All operations work in the user's local timezone —
 * the calendar is a presentation layer; UTC↔local conversion happens at the
 * API boundary (events store start_at as ISO 8601 / UTC, the calendar
 * renders in `new Date(...)` local).
 *
 * Pure functions; no side effects. Tests in calendar-utils.test.ts.
 */

export const WEEKDAY_LABELS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export const WEEKDAY_LABELS_NARROW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

export const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** Midnight at the start of `d` (local timezone). */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

export function addMonths(d: Date, n: number): Date {
  // Anchor to day 1 to avoid overflow surprises (Jan 31 + 1 month).
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

/**
 * Returns the 6×7 grid of days for the month containing `focus`.
 * Sundays first. Always exactly 42 cells; leading/trailing cells fall in
 * the previous/next months (typical month-grid layout).
 */
export function monthGrid(focus: Date): Date[][] {
  const first = startOfMonth(focus);
  const leading = first.getDay(); // 0 = Sunday
  const start = addDays(first, -leading);
  const rows: Date[][] = [];
  for (let row = 0; row < 6; row += 1) {
    const r: Date[] = [];
    for (let col = 0; col < 7; col += 1) {
      r.push(addDays(start, row * 7 + col));
    }
    rows.push(r);
  }
  return rows;
}

/**
 * Sunday-anchored 7-day window containing `focus`. Sunday at index 0.
 */
export function weekGrid(focus: Date): Date[] {
  const day = startOfDay(focus);
  const start = addDays(day, -day.getDay());
  const out: Date[] = [];
  for (let i = 0; i < 7; i += 1) out.push(addDays(start, i));
  return out;
}

/** "Week of Sun May 10" → "Sun May 10 – Sat May 16" */
export function formatWeekRange(focus: Date): string {
  const week = weekGrid(focus);
  const a = week[0]!;
  const b = week[6]!;
  if (a.getMonth() === b.getMonth()) {
    return `${MONTH_LABELS[a.getMonth()]} ${a.getDate()} – ${b.getDate()}, ${a.getFullYear()}`;
  }
  return `${MONTH_LABELS[a.getMonth()]!.slice(0, 3)} ${a.getDate()} – ${MONTH_LABELS[b.getMonth()]!.slice(0, 3)} ${b.getDate()}, ${a.getFullYear()}`;
}

/** "September 2026" */
export function formatMonthYear(d: Date): string {
  return `${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "Tuesday, September 22" */
export function formatLongDay(d: Date): string {
  const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][
    d.getDay()
  ];
  return `${weekday}, ${MONTH_LABELS[d.getMonth()]} ${d.getDate()}`;
}

/** "9:50 AM" */
export function formatTime(d: Date): string {
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * Filter `events` to those whose start_at falls on `day` (local).
 * For multi-day events (start ≤ day ≤ end), include them on every day
 * in the range. The legacy app's events were almost always single-day,
 * but the schema allows multi-day so we handle it correctly.
 */
export interface CalendarEvent {
  id: string;
  title: string;
  start_at: string;
  end_at: string;
  status: string;
}

/**
 * "All-day" predicate. The schema has no is_all_day flag; we infer from
 * duration. Anything 24h or longer is treated as all-day for layout
 * purposes — true all-day events (midnight to midnight) and multi-day
 * spans both qualify. Sub-day events that happen to cross midnight
 * (e.g., 11pm–2am) stay in the time grid clamped at day boundaries.
 */
export function isAllDayEvent(event: { start_at: string; end_at: string }): boolean {
  const startMs = new Date(event.start_at).getTime();
  const endMs = new Date(event.end_at).getTime();
  return endMs - startMs >= 24 * 60 * 60 * 1000;
}

export function eventsOnDay<T extends CalendarEvent>(events: T[], day: Date): T[] {
  const dayStart = startOfDay(day).getTime();
  const dayEnd = endOfDay(day).getTime();
  return events
    .filter((e) => {
      const s = new Date(e.start_at).getTime();
      const en = new Date(e.end_at).getTime();
      return s <= dayEnd && en >= dayStart;
    })
    .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
}

/**
 * Pack overlapping events into side-by-side lanes (Google-Calendar style).
 * Events that don't overlap keep full width. Events that overlap form a
 * "cluster" — every event in the cluster gets the same `lanes` count
 * (= cluster width divisor) but a unique `lane` index in [0, lanes).
 *
 * Algorithm: sort by start, walk through forming clusters of transitively
 * overlapping events, then greedy-pack each cluster into the smallest set
 * of lanes such that no two events in the same lane overlap.
 *
 * Returns a Map keyed by event id. Events not in the input get nothing.
 */
export function assignLanes<T extends CalendarEvent>(
  events: T[],
): Map<string, { lane: number; lanes: number }> {
  const result = new Map<string, { lane: number; lanes: number }>();
  if (events.length === 0) return result;
  const sorted = events.slice().sort((a, b) => a.start_at.localeCompare(b.start_at));
  let i = 0;
  while (i < sorted.length) {
    const cluster: T[] = [sorted[i]!];
    let clusterEnd = sorted[i]!.end_at;
    let j = i + 1;
    while (j < sorted.length && sorted[j]!.start_at < clusterEnd) {
      cluster.push(sorted[j]!);
      if (sorted[j]!.end_at > clusterEnd) clusterEnd = sorted[j]!.end_at;
      j += 1;
    }
    const laneEnds: string[] = [];
    const laneOf = new Map<string, number>();
    for (const e of cluster) {
      let chosen = -1;
      for (let k = 0; k < laneEnds.length; k += 1) {
        if (laneEnds[k]! <= e.start_at) {
          laneEnds[k] = e.end_at;
          chosen = k;
          break;
        }
      }
      if (chosen === -1) {
        chosen = laneEnds.length;
        laneEnds.push(e.end_at);
      }
      laneOf.set(e.id, chosen);
    }
    const lanes = laneEnds.length;
    for (const e of cluster) {
      result.set(e.id, { lane: laneOf.get(e.id)!, lanes });
    }
    i = j;
  }
  return result;
}
